import { useEffect, useRef, useState, useCallback } from 'react';

// Type definitions for Wake Lock API since they might not be available in older TypeScript versions
declare global {
  interface Navigator {
    wakeLock?: {
      request: (type: 'screen') => Promise<WakeLockSentinel>;
    };
  }

  interface WakeLockSentinel extends EventTarget {
    readonly released: boolean;
    readonly type: 'screen';
    release(): Promise<void>;
    addEventListener(type: 'release', listener: () => void): void;
    removeEventListener(type: 'release', listener: () => void): void;
  }
}

type WakeLockState = {
  isSupported: boolean;
  isActive: boolean;
  error: string | null;
};

export const useWakeLock = () => {
  const [state, setState] = useState<WakeLockState>({
    isSupported: typeof navigator !== 'undefined' && 'wakeLock' in navigator,
    isActive: false,
    error: null
  });

  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const requestWakeLock = useCallback(async () => {
    if (!state.isSupported) {
      setState(prev => ({ ...prev, error: 'Screen Wake Lock API is not supported in this browser' }));
      return;
    }

    try {
      // Request a screen wake lock
      wakeLockRef.current = await navigator.wakeLock!.request('screen');
      
      setState(prev => ({
        ...prev,
        isActive: true,
        error: null
      }));

      // Listen for the wake lock to be released
      wakeLockRef.current.addEventListener('release', () => {
        setState(prev => ({ ...prev, isActive: false }));
      });

    } catch (error: any) {
      console.error('Failed to request wake lock:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to request wake lock'
      }));
    }
  }, [state.isSupported]);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (error: any) {
        console.error('Failed to release wake lock:', error);
      }
    }
  }, []);

  // Handle page visibility changes - reacquire wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (state.isActive && document.visibilityState === 'visible' && !wakeLockRef.current) {
        // Reacquire the wake lock when the page becomes visible again
        requestWakeLock();
      }
    };

    if (state.isSupported) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [state.isActive, state.isSupported, requestWakeLock]);

  // Clean up wake lock on unmount
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  return {
    ...state,
    requestWakeLock,
    releaseWakeLock
  };
};